import React, { forwardRef, useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  Typography,
  Stack,
} from "@mui/material";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AutoSelectbox } from "../../components/selectbox/autoselect";
import { ENUM_AUTOSELECT_LIST_TYPES } from "../../enums";
import { getAmbassadorsListFromSearch } from "../../apis/ambassadors.api";
import { searchVendorCompanyWithoutDelegate } from "../../apis/clients.api";
import { convertToFullName } from "../../services/utils";
import { useDispatch } from "react-redux";
import { CONST_PROJECT_STATUS_LIST } from "../../constants/lists";
import FlexBox from "../../components/layouts/flexbox";

interface ActivityLogFilterValues {
  dateRange: { startDate: Date | null; endDate: Date | null };
  ambassador: any;
  vendor: any;
  status: any;
}

const VISITED_FILTER_LIST = [
  { label: "All", value: "all" },
  { label: "Visited", value: "visited" },
  { label: "Unvisited", value: "unvisited" },
];

interface ActivityLogFiltersProps {
  onFiltersChange?: (values: ActivityLogFilterValues) => void;
  visitedFilter?: string;
  onVisitedFilterChange?: (visitedFilter: string) => void;
}

export default function ActivityLogFilters({
  onFiltersChange,
  visitedFilter = "all",
  onVisitedFilterChange,
}: ActivityLogFiltersProps) {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);

  // Ambassador search
  const [selectedAmbassador, setSelectedAmbassador] = useState<any>(null);
  const [ambassadorList, setAmbassadorList] = useState<any[]>([]);

  // Vendor search
  const [selectedVendor, setSelectedVendor] = useState<any>(null);
  const [vendorList, setVendorList] = useState<any[]>([]);

  // Status filter
  const [status, setStatus] = useState<any>(null);

  const [selectedVisitedFilter, setSelectedVisitedFilter] = useState(
    () =>
      VISITED_FILTER_LIST.find((item) => item.value === visitedFilter) ||
      VISITED_FILTER_LIST[0]
  );

  // Convert CONST_PROJECT_STATUS_LIST to array for AutoSelectbox
  const statusList = Object.keys(CONST_PROJECT_STATUS_LIST).map((key) => ({
    label: CONST_PROJECT_STATUS_LIST[key].auditLogLabel,
    value: key,
  }));

  const handleAmbassadorChange = async (e: any) => {
    const text = e?.target?.value;

    if (typeof text === "object") {
      setSelectedAmbassador(text);
      emitChange({ ambassador: text });
      return;
    }

    const res: any = await dispatch(getAmbassadorsListFromSearch(text));

    if (res.success) {
      const list = res?.data?.map((r: any) => {
        return {
          fullName: convertToFullName(r?.firstName, r?.lastName),
          id: r?.id,
        };
      });
      setAmbassadorList(list);
    }
  };

  const handleVendorChange = async (e: any) => {
    const text = e?.target?.value;

    if (typeof text === "object") {
      setSelectedVendor(text);
      emitChange({ vendor: text });
      return;
    }

    if (!text || text?.length < 3) {
      setVendorList([]);
      return;
    }

    const res: any = await searchVendorCompanyWithoutDelegate({
      selectedPage: 1,
      selectedPageSize: 10,
      searchValue: text,
    });

    if (res.success) {
      setVendorList(res.data);
    }
  };

  const handleStatusChange = (e: any) => {
    const value = e?.target?.value;

    if (typeof value === "object") {
      setStatus(value);
      emitChange({ status: value });
    }
  };

  const handleVisitedFilterChange = (e: any) => {
    const value = e?.target?.value;

    if (typeof value === "object") {
      setSelectedVisitedFilter(value);
      onVisitedFilterChange?.(value?.value);
    }
  };

  const emitChange = (values?: Partial<ActivityLogFilterValues>) => {
    if (!onFiltersChange) return;
    onFiltersChange({
      dateRange: { startDate: dateRange[0], endDate: dateRange[1] },
      ambassador: selectedAmbassador,
      vendor: selectedVendor,
      status,
      ...(values || {}),
    });
  };

  return (
    <Grid container spacing={2} alignItems={"center"}>
      <Grid item xs={12} md={6}>
        <Grid container spacing={2} alignItems={"center"}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="medium">
              <Typography fontSize={14} fontWeight={400}>
                Ambassador
              </Typography>
              <AutoSelectbox
                label={""}
                fullWidth={true}
                field={"ambassadorSearch"}
                id={"ambassadorSearch"}
                inlineLabel={false}
                textfieldStyle={{ width: "100%" as any }}
                list={ambassadorList || []}
                onChange={handleAmbassadorChange}
                value={selectedAmbassador}
                error={""}
                listProperties={{
                  field: "fullName",
                  value: "id",
                }}
                listType={ENUM_AUTOSELECT_LIST_TYPES.ARRAY_OF_OBJECT}
                requireOnChange={true}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="medium">
              <Typography fontSize={14} fontWeight={400}>
                Vendor Company
              </Typography>
              <AutoSelectbox
                label={""}
                fullWidth={true}
                field={"vendorSearch"}
                id={"vendorSearch"}
                inlineLabel={false}
                textfieldStyle={{ width: "100%" as any }}
                list={vendorList || []}
                onChange={handleVendorChange}
                value={selectedVendor}
                error={""}
                listProperties={{
                  field: "company",
                  value: "id",
                }}
                listType={ENUM_AUTOSELECT_LIST_TYPES.ARRAY_OF_OBJECT}
                requireOnChange={true}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="medium">
              <Typography fontSize={14} fontWeight={400}>
                Status
              </Typography>
              <AutoSelectbox
                label={""}
                fullWidth={true}
                field={"status"}
                id={"status"}
                inlineLabel={false}
                textfieldStyle={{ width: "100%" as any }}
                list={statusList || []}
                onChange={handleStatusChange}
                value={status}
                error={""}
                listProperties={{
                  field: "label",
                  value: "value",
                }}
                listType={ENUM_AUTOSELECT_LIST_TYPES.ARRAY_OF_OBJECT}
                requireOnChange={true}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6} container justifyContent={"flex-end"}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="flex-end"
          width="100%"
        >
          <FormControl size="medium" sx={{ width: { xs: "100%", sm: 120 } }}>
            <Typography fontSize={14} fontWeight={400}>
              Visited
            </Typography>
            <AutoSelectbox
              label={""}
              fullWidth={true}
              field={"visitedFilter"}
              id={"visitedFilter"}
              inlineLabel={false}
              textfieldStyle={{ width: "100%", maxWidth: 120 } as any}
              list={VISITED_FILTER_LIST}
              onChange={handleVisitedFilterChange}
              value={selectedVisitedFilter}
              error={""}
              listProperties={{
                field: "label",
                value: "value",
              }}
              listType={ENUM_AUTOSELECT_LIST_TYPES.ARRAY_OF_OBJECT}
              requireOnChange={true}
            />
          </FormControl>
          <FlexBox justifyContent={"flex-end"} width={{ xs: "100%", sm: "50%" }}>
          <RangeDateInput
            value={dateRange}
            onChange={(newRange) => {
              setDateRange(newRange);
              if (newRange[0] && newRange[1]) {
                emitChange({
                  dateRange: { startDate: newRange[0], endDate: newRange[1] },
                });
              }

              if (!newRange[0] && !newRange[1]) {
                emitChange({
                  dateRange: { startDate: null, endDate: null },
                });
              }
            }}
          />
          </FlexBox>
        </Stack>
      </Grid>
    </Grid>
  );
}

const RangeDateInput = ({
  value,
  onChange,
}: {
  value: [Date | null, Date | null];
  onChange: (val: [Date | null, Date | null]) => void;
}) => {
  const [startDate, endDate] = value;

  const CustomInput = forwardRef(function CustomInput(props: any, ref: any) {
    return (
      <TextField
        {...props}
        inputRef={ref}
        size="medium"
        fullWidth
        InputProps={{
          sx: { height: 50 },
          endAdornment: props?.InputProps?.endAdornment,
        }}
      />
    );
  });

  return (
    <FormControl fullWidth size="medium">
      <Typography fontSize={14} fontWeight={400}>
        Date range
      </Typography>
      <ReactDatePicker
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(update: [Date | null, Date | null]) => {
          onChange(update);
        }}
        isClearable
        customInput={<CustomInput />}
        dateFormat="MMM d, yyyy"
        maxDate={new Date()}
      />
    </FormControl>
  );
};
